import React, { FC, useEffect, useRef } from 'react';

import { reaction } from 'mobx';
import { observer } from 'mobx-react';

import { useStores, useUploadNavigationGuard } from '../../hooks';
import MyFilesUploadPopup from '.';
import UploadNavigationWarningModal from './UploadNavigationWarningModal';

// Backend indexing of a freshly uploaded file can lag slightly behind the
// upload completing, so the per-item insert may miss it. Once the whole batch
// finishes we refetch the current folder to guarantee every file shows up.
const UPLOAD_COMPLETE_REFRESH_DELAY_MS = 1500;

/**
 * Glue between the upload engine and the My Files page:
 * - keeps the My Files list in sync with completed uploads and selects them,
 * - renders the bottom-right upload popup,
 * - guards navigation while uploads are in progress.
 */
const MyFilesUploadController: FC = observer(() => {
    const { appStore, filesListStore, uploadFilesStore } = useStores();

    const refreshTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

    // Note: surfacing freshly uploaded files into the list is owned by
    // FilesListStore.syncNewlyUploadedFiles (wired in AppStore), which is
    // deduped per fid. We deliberately do NOT run a second, un-deduped
    // onUploadNewFiles here - it would re-insert still-present drawer items
    // whenever the drawer set changes (e.g. re-adding rows mid delete).

    // Keep every successfully uploaded item that is present in the list selected.
    // Keyed on the *present* uploaded fids so a row that appears later (after the
    // list catches up) still gets checked, and is not lost to syncSelectedItems
    // pruning rows that were not yet in the list.
    useEffect(() => reaction(
        () => {
            if (!appStore.isMyFilesUploadPopupOpen) {
                return '';
            }
            const presentIds = new Set(filesListStore.filesToDisplay.map(({ fid }) => fid));
            return uploadFilesStore.uploadedFileIds.filter((fid) => presentIds.has(fid)).join(',');
        },
        (key) => {
            if (!appStore.isMyFilesUploadPopupOpen) {
                return;
            }
            filesListStore.setCurrentSelectedFiles(key ? key.split(',') : []);
        },
    ), [appStore, filesListStore, uploadFilesStore]);

    // When the whole batch finishes uploading, nudge the background refresher to
    // do one immediate top-only refresh so any file the backend had not yet
    // indexed (and was therefore missing from the per-item insert) appears
    // without waiting for the next scheduled tick. The activity-driven refresher
    // stays active around the upload, so anything still lagging is picked up on a
    // subsequent tick. We intentionally avoid refreshFiles() here: a full reset
    // would clear the active search and compete with that background refresher.
    useEffect(() => {
        const dispose = reaction(
            () => uploadFilesStore.isUploading,
            (isUploading, wasUploading) => {
                if (!wasUploading || isUploading || !uploadFilesStore.uploadedFileIds.length) {
                    return;
                }
                if (refreshTimeoutRef.current) {
                    clearTimeout(refreshTimeoutRef.current);
                }
                refreshTimeoutRef.current = setTimeout(() => {
                    if (!uploadFilesStore.isUploading && appStore.isMyFilesUploadPopupOpen) {
                        filesListStore.refreshActiveViewNow();
                    }
                }, UPLOAD_COMPLETE_REFRESH_DELAY_MS);
            },
        );
        return () => {
            dispose();
            if (refreshTimeoutRef.current) {
                clearTimeout(refreshTimeoutRef.current);
            }
        };
    }, [appStore, filesListStore, uploadFilesStore]);

    // If the drawer is emptied from outside the popup (e.g. every uploaded item
    // is removed via "Delete selected" in My Files), there is nothing left to
    // show - tear the popup down and clear selection. Guarded on a non-empty ->
    // empty transition so it never fires on the initial open (before appendFile
    // has populated the first item).
    useEffect(() => reaction(
        () => uploadFilesStore.modifiedFoldedFiles.length,
        (count, prevCount) => {
            if (
                prevCount > 0
                && count === 0
                && !uploadFilesStore.isUploading
                && appStore.isMyFilesUploadPopupOpen
            ) {
                filesListStore.setCurrentSelectedFiles([]);
                uploadFilesStore.clear();
                appStore.closeMyFilesUploadPopup();
            }
        },
    ), [appStore, filesListStore, uploadFilesStore]);

    // The My Files / Shared / All Files panes all render this controller (same
    // component, different route key), and the popup open-state is global, so the
    // drawer would otherwise re-appear on a sibling pane. Tear it down when the
    // page unmounts (i.e. the user leaves the pane). The navigation guard should
    // block leaving while uploading, but abort any in-flight requests defensively
    // — uploads must never continue in the background after navigation — and only
    // wipe the store once the aborts have run (clear() first would drop the item
    // references cancelActiveUploads needs).
    useEffect(() => () => {
        if (appStore.isMyFilesUploadPopupOpen) {
            filesListStore.setCurrentSelectedFiles([]);
            appStore.closeMyFilesUploadPopup();
            uploadFilesStore.cancelActiveUploads()
                .finally(() => uploadFilesStore.clear());
        }
    }, [appStore, filesListStore, uploadFilesStore]);

    const cancelAllForNavigation = async (): Promise<void> => {
        await uploadFilesStore.cancelActiveUploads();
        uploadFilesStore.clear();
        filesListStore.setCurrentSelectedFiles([]);
        appStore.closeMyFilesUploadPopup();
    };

    const { warningOpen, confirmCancelAll, dismiss } = useUploadNavigationGuard(
        uploadFilesStore.isUploading,
        cancelAllForNavigation,
    );

    return (
        <>
            {appStore.isMyFilesUploadPopupOpen && <MyFilesUploadPopup />}
            <UploadNavigationWarningModal
                isOpen={warningOpen}
                onConfirm={confirmCancelAll}
                onCancel={dismiss}
            />
        </>
    );
});

export default MyFilesUploadController;
